import type { Backtest } from "../../../types/backtests";
import type {
  Report,
  ReportStrategyDetailResponse,
  ReportStrategyPolicyVersion,
  ReportStrategyResponse,
} from "../../../types/reports";
import type {
  WorkspaceNode,
  WorkspaceNodeMeta,
  WorkspaceNodeMetaAccount,
  WorkspaceNodeMetaSourcePolicy,
  WorkspaceNodeType,
} from "../../../types/workspace";
import { WORKSPACE_NODE_CARD_TITLE_MAX_LENGTH } from "./workspaceGraphConstants";
import {
  formatSignedNumber,
  metricDeltaNumber,
  normalizeWinRatePercent,
} from "../ExecutionRulesSimulator/metricFormatters";
import {
  extractReportCohortSummary,
  getReportCandleTimeframeForDisplay,
  getReportCohortWindowLabel,
} from "../../../utils/reportListDisplay";
import { getReportTypeDisplayLabel, resolveReportTypeDisplayLabel } from "../../../utils/reportTypeRules";
import { getReportTypeBadgeDataType } from "../Reports/components/ReportsCardChrome";
import { getReportDisplayTitle } from "../../../utils/reportWindowLabel";
import {
  masterPineUploadStatusLabel,
  PINE_SCRIPT_CREATED_LABEL,
  PINE_SCRIPT_NOT_CREATED_LABEL,
  PINE_SCRIPT_NOT_UPLOADED_LABEL,
  PINE_SCRIPT_UPLOADED_LABEL,
  strategyHasMasterPinescriptUploaded,
  strategyHasTradingViewPinescriptCreated,
  tradingViewPineCreatedStatusLabel,
} from "../../../utils/reportStrategyPineUi";
import {
  resolvePolicyKindsFromVersion,
  resolvePolicyKindsFromWorkspaceMeta,
  type PolicyKind,
} from "../../../utils/policyKindChips";
import { policyVersionDisplayTitle } from "../Strategies/strategyDetailUtils";

export type WorkspaceEntityCardDetail =
  | {
      kind: "strategy";
      title: string;
      description: string;
      updatedAt?: string;
      hasMasterPine: boolean;
      hasActivePine: boolean;
      masterPineLabel: string;
      activePineLabel: string;
      /** Non-archived policy versions for the card metadata chip. */
      policyVersionCount: number;
      accounts: WorkspaceNodeMetaAccount[];
    }
  | {
      kind: "report";
      title: string;
      reportType: string;
      /** API report type key for type-badge gradients. */
      reportTypeKey: string;
      createdAt?: string;
      windowLabel: string;
      timeframe: string;
      processing: boolean;
      failed: boolean;
      totalPnl: number | null;
      totalTrades: number | null;
      winRate: number | null;
    }
  | {
      kind: "simulation";
      title: string;
      originLabel: string;
      isFromReport: boolean;
      createdAt?: string;
      pnlDelta: number | null;
      winRateDelta: number | null;
      /**
       * Primary trades metric for the card column.
       * Prefer Exit Placement `tradesChanged`; else RA net `tradeCount` delta.
       */
      tradesDelta: number | null;
      /** Net removals (delta.trade_count / meta.tradeCount). */
      tradesRemoved: number | null;
      /** EP impact (delta.trades_changed / meta.tradesChanged). */
      tradesChanged: number | null;
      /** Label for the primary trades column. */
      tradesDeltaLabel: string;
      /** Candle TF from node meta (may be dual-TF `4h→1m` or multi `1m · 5m`). */
      timeframe: string;
      /** Distinct TFs when BE sends `candleTimeframes` (finest-first). */
      timeframes: string[];
      /** Cohort window from meta (concrete YMD range). */
      windowLabel: string;
    }
  | {
      kind: "policy";
      title: string;
      createdAt?: string;
      policyTree: unknown;
      accounts: WorkspaceNodeMetaAccount[];
      versionNumber: number | null;
      isCombined: boolean;
      isCrossAccountCombined: boolean;
      sourcePolicies: WorkspaceNodeMetaSourcePolicy[];
      status?: string;
      /** Candle TF from node meta. */
      timeframe: string;
      /** Distinct TFs when BE sends `candleTimeframes` (finest-first). */
      timeframes: string[];
      /** Keep / Remove / SL / TP chips from node meta. */
      policyKinds: PolicyKind[];
    };

export type WorkspaceEntityCardDetailsMap = Record<string, WorkspaceEntityCardDetail>;

export function workspaceEntityCardDetailKey(
  type: WorkspaceNodeType,
  entityId: string
): string {
  return `${type}:${entityId.trim()}`;
}

function finiteOrNull(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

/**
 * Resolve simulation trade / PnL deltas from `result.delta` when present,
 * falling back to baseline vs filtered metrics for older saved sims.
 */
export function simulationSummaryDeltas(backtest: Backtest): {
  pnlDelta: number | null;
  winRateDelta: number | null;
  tradesRemoved: number | null;
  tradesChanged: number | null;
  tradesDelta: number | null;
  tradesDeltaLabel: string;
} {
  const baseline = backtest.result?.baseline;
  const filtered = backtest.result?.filtered;
  const delta = backtest.result?.delta;
  const deltaRec =
    delta && typeof delta === "object" ? (delta as Record<string, unknown>) : null;
  const winRateBaseline = normalizeWinRatePercent(baseline?.win_rate);
  const winRateFiltered = normalizeWinRatePercent(filtered?.win_rate);

  const tradesRemoved =
    finiteOrNull(delta?.trade_count ?? deltaRec?.trade_count) ??
    metricDeltaNumber(baseline?.trade_count, filtered?.trade_count);
  const tradesChanged = finiteOrNull(
    delta?.trades_changed ?? deltaRec?.trades_changed ?? deltaRec?.tradesChanged
  );
  const pnlDelta =
    finiteOrNull(delta?.total_pnl ?? deltaRec?.total_pnl ?? deltaRec?.totalPnl) ??
    metricDeltaNumber(baseline?.total_pnl, filtered?.total_pnl);

  const primary = resolvePrimarySimulationTradesMetric(tradesRemoved, tradesChanged);

  return {
    pnlDelta,
    winRateDelta:
      winRateBaseline == null || winRateFiltered == null
        ? null
        : winRateFiltered - winRateBaseline,
    tradesRemoved,
    tradesChanged,
    tradesDelta: primary.value,
    tradesDeltaLabel: primary.label,
  };
}

/** Prefer Exit Placement `tradesChanged`; else RA net removals via `tradeCount`. */
export function resolvePrimarySimulationTradesMetric(
  tradesRemoved: number | null,
  tradesChanged: number | null
): { value: number | null; label: string } {
  if (tradesChanged != null) {
    return { value: tradesChanged, label: "Trades changed" };
  }
  return { value: tradesRemoved, label: "Trades" };
}

export function simulationTradesMetricsFromMeta(meta?: WorkspaceNodeMeta): {
  tradesRemoved: number | null;
  tradesChanged: number | null;
  tradesDelta: number | null;
  tradesDeltaLabel: string;
} {
  const tradesRemoved = meta?.tradeCount ?? null;
  const tradesChanged = meta?.tradesChanged ?? null;
  const primary = resolvePrimarySimulationTradesMetric(tradesRemoved, tradesChanged);
  return {
    tradesRemoved,
    tradesChanged,
    tradesDelta: primary.value,
    tradesDeltaLabel: primary.label,
  };
}

function metaAccounts(meta?: WorkspaceNodeMeta): WorkspaceNodeMetaAccount[] {
  const accounts = meta?.accounts;
  return Array.isArray(accounts) ? accounts : [];
}

/**
 * Distinct candle TFs from meta (finest-first when BE sends `candleTimeframes`).
 * Falls back to dual-TF entry/management or the primary scalar.
 */
export function resolveWorkspaceMetaCandleTimeframes(
  meta?: WorkspaceNodeMeta | null
): string[] {
  if (!meta) return [];
  const fromList = (meta.candleTimeframes ?? [])
    .map((tf) => tf.trim())
    .filter(Boolean);
  if (fromList.length > 0) return fromList;

  const entry = meta.entryCandleTimeframe?.trim() || "";
  const management = meta.managementCandleTimeframe?.trim() || "";
  if (entry && management && entry !== management) {
    return [entry, management];
  }
  const primary =
    meta.candleTimeframe?.trim() ||
    meta.resolvedCandleTimeframe?.trim() ||
    entry ||
    management ||
    "";
  return primary ? [primary] : [];
}

/**
 * Compact candle TF label for canvas chips.
 * Multi entry-segment: `1m · 5m`. Dual-TF without list: `4h→1m`. Else primary.
 */
export function formatWorkspaceMetaCandleTimeframe(
  meta?: WorkspaceNodeMeta | null
): string | null {
  if (!meta) return null;
  const listed = (meta.candleTimeframes ?? [])
    .map((tf) => tf.trim())
    .filter(Boolean);
  if (listed.length > 1) return listed.join(" · ");
  if (listed.length === 1) return listed[0] ?? null;

  const entry = meta.entryCandleTimeframe?.trim() || "";
  const management = meta.managementCandleTimeframe?.trim() || "";
  if (entry && management && entry !== management) {
    return `${entry}→${management}`;
  }
  const primary =
    meta.candleTimeframe?.trim() ||
    meta.resolvedCandleTimeframe?.trim() ||
    entry ||
    management ||
    "";
  return primary || null;
}

/**
 * Report cohort window chip from meta (not candle TF).
 * Prefers BE `windowLabel` (concrete YMD range), then `localStartDate`/`localEndDate`.
 * Does not humanize cohort `timeframe` presets ("All time", "Last 30 days").
 */
export function formatWorkspaceMetaWindowLabel(
  meta?: WorkspaceNodeMeta | null
): string | null {
  if (!meta) return null;
  const label = meta.windowLabel?.trim();
  if (label) return label;
  const start = meta.localStartDate?.trim() || "";
  const end = meta.localEndDate?.trim() || "";
  if (start && end) {
    return start === end ? start : `${start} – ${end}`;
  }
  return null;
}

function isProcessingStatus(status: WorkspaceNodeMeta["status"] | undefined): boolean {
  return status === "processing" || status === "draft";
}

/**
 * Build canvas card chrome from workspace node.meta only — no detail GET.
 * Missing stats render as processing / unavailable rather than triggering fetches.
 */
export function buildEntityCardDetailFromMeta(
  type: WorkspaceNodeType,
  meta: WorkspaceNodeMeta | undefined
): WorkspaceEntityCardDetail | null {
  const title = meta?.title?.trim() || "";

  if (type === "strategy") {
    const hasMasterPine = Boolean(meta?.hasMasterPine);
    const hasActivePine = Boolean(meta?.hasActivePine);
    const policyVersionCount =
      typeof meta?.policyVersionCount === "number" && Number.isFinite(meta.policyVersionCount)
        ? Math.max(0, Math.trunc(meta.policyVersionCount))
        : 0;
    return {
      kind: "strategy",
      title: title || "Untitled strategy",
      description: meta?.subtitle?.trim() || "—",
      updatedAt: meta?.updatedAt?.trim() || meta?.createdAt?.trim() || undefined,
      hasMasterPine,
      hasActivePine,
      masterPineLabel: hasMasterPine ? PINE_SCRIPT_UPLOADED_LABEL : PINE_SCRIPT_NOT_UPLOADED_LABEL,
      activePineLabel: hasActivePine ? PINE_SCRIPT_CREATED_LABEL : PINE_SCRIPT_NOT_CREATED_LABEL,
      policyVersionCount,
      accounts: metaAccounts(meta),
    };
  }

  if (type === "report") {
    const processing = isProcessingStatus(meta?.status);
    const totalPnl = meta?.pnl ?? null;
    const totalTrades = meta?.tradeCount ?? null;
    const winRate = meta?.winRate ?? null;
    const reportTypeKey = getReportTypeBadgeDataType(
      meta?.reportType || meta?.reportTypeLabel
    );
    return {
      kind: "report",
      title: title || "Untitled report",
      reportType:
        meta?.reportType?.trim() || meta?.reportTypeLabel?.trim()
          ? resolveReportTypeDisplayLabel(meta?.reportType, meta?.reportTypeLabel)
          : "Report",
      reportTypeKey,
      createdAt: meta?.createdAt,
      windowLabel: formatWorkspaceMetaWindowLabel(meta) || "—",
      timeframe: formatWorkspaceMetaCandleTimeframe(meta) || "—",
      processing,
      failed: meta?.status === "failed",
      totalPnl,
      totalTrades,
      winRate,
    };
  }

  if (type === "simulation" || type === "combined_simulation") {
    const trades = simulationTradesMetricsFromMeta(meta);
    return {
      kind: "simulation",
      title: title || "Untitled simulation",
      originLabel: "Simulation",
      isFromReport: false,
      createdAt: meta?.createdAt,
      pnlDelta: meta?.pnlChange ?? null,
      winRateDelta: meta?.winRateDelta ?? null,
      tradesDelta: trades.tradesDelta,
      tradesRemoved: trades.tradesRemoved,
      tradesChanged: trades.tradesChanged,
      tradesDeltaLabel: trades.tradesDeltaLabel,
      timeframe: formatWorkspaceMetaCandleTimeframe(meta) || "—",
      timeframes: resolveWorkspaceMetaCandleTimeframes(meta),
      windowLabel: formatWorkspaceMetaWindowLabel(meta) || "—",
    };
  }

  if (type === "policy" || type === "combined_policy") {
    const sourcePolicies = meta?.sourcePolicies;
    return {
      kind: "policy",
      title: title || "",
      createdAt: meta?.createdAt,
      policyTree: null,
      accounts: metaAccounts(meta),
      versionNumber: meta?.versionNumber ?? null,
      isCombined: Boolean(meta?.isCombined) || type === "combined_policy",
      isCrossAccountCombined: Boolean(meta?.isCrossAccountCombined),
      sourcePolicies: Array.isArray(sourcePolicies) ? sourcePolicies : [],
      status: meta?.status,
      timeframe: formatWorkspaceMetaCandleTimeframe(meta) || "—",
      timeframes: resolveWorkspaceMetaCandleTimeframes(meta),
      policyKinds: resolvePolicyKindsFromWorkspaceMeta(meta),
    };
  }

  return null;
}

export function buildStrategyEntityCardDetail(
  strategy: ReportStrategyResponse | ReportStrategyDetailResponse
): WorkspaceEntityCardDetail {
  const hasMaster = strategyHasMasterPinescriptUploaded(strategy);
  const hasActive = strategyHasTradingViewPinescriptCreated(strategy);
  const versions: ReportStrategyPolicyVersion[] =
    "policy_versions" in strategy && Array.isArray(strategy.policy_versions)
      ? strategy.policy_versions
      : [];
  const policyVersionCount = versions.filter((version) => !version.is_archived).length;
  return {
    kind: "strategy",
    title: strategy.name?.trim() || "Untitled strategy",
    description: strategy.description?.trim() || "—",
    updatedAt: strategy.updated_at,
    hasMasterPine: hasMaster,
    hasActivePine: hasActive,
    masterPineLabel: masterPineUploadStatusLabel(strategy),
    activePineLabel: tradingViewPineCreatedStatusLabel(strategy),
    policyVersionCount,
    accounts: Array.isArray(strategy.accounts) ? strategy.accounts : [],
  };
}

export function buildReportEntityCardDetail(report: Report): WorkspaceEntityCardDetail {
  const rangeStats = extractReportCohortSummary(report);
  const isPending = String(report.id).startsWith("pending-");
  const processing = isPending || report.status === "processing";
  return {
    kind: "report",
    title: getReportDisplayTitle(report),
    reportType: getReportTypeDisplayLabel(report.report_type),
    reportTypeKey: getReportTypeBadgeDataType(report.report_type),
    createdAt: report.created_at,
    windowLabel: getReportCohortWindowLabel(report),
    timeframe: getReportCandleTimeframeForDisplay(report)?.trim() || "—",
    processing,
    failed: false,
    totalPnl: rangeStats?.totalPnl ?? null,
    totalTrades: rangeStats?.totalTrades ?? null,
    winRate: rangeStats?.winRate ?? null,
  };
}

export function buildSimulationEntityCardDetail(backtest: Backtest): WorkspaceEntityCardDetail {
  const reportId = backtest.report_id?.trim() || "";
  const isFromReport = Boolean(reportId);
  const {
    pnlDelta,
    winRateDelta,
    tradesDelta,
    tradesRemoved,
    tradesChanged,
    tradesDeltaLabel,
  } = simulationSummaryDeltas(backtest);
  return {
    kind: "simulation",
    title: backtest.title?.trim() || "Untitled simulation",
    originLabel: isFromReport ? "From report" : "Standalone",
    isFromReport,
    createdAt: backtest.created_at,
    pnlDelta,
    winRateDelta,
    tradesDelta,
    tradesRemoved,
    tradesChanged,
    tradesDeltaLabel,
    timeframe: backtest.scope?.candle_timeframe?.trim() || "—",
    timeframes: backtest.scope?.candle_timeframe?.trim()
      ? [backtest.scope.candle_timeframe.trim()]
      : [],
    windowLabel:
      backtest.scope?.window_label?.trim() ||
      (backtest.scope?.local_start_date?.trim() &&
      backtest.scope?.local_end_date?.trim()
        ? backtest.scope.local_start_date.trim() ===
          backtest.scope.local_end_date.trim()
          ? backtest.scope.local_start_date.trim()
          : `${backtest.scope.local_start_date.trim()} – ${backtest.scope.local_end_date.trim()}`
        : "—"),
  };
}

export function buildPolicyEntityCardDetail(version: ReportStrategyPolicyVersion): WorkspaceEntityCardDetail {
  const sourceIds = version.source_policy_version_ids;
  return {
    kind: "policy",
    title: policyVersionDisplayTitle(version),
    createdAt: version.created_at,
    policyTree: version.policy_tree ?? null,
    accounts: Array.isArray(version.accounts) ? version.accounts : [],
    versionNumber: version.version_number ?? null,
    isCombined: Array.isArray(sourceIds) && sourceIds.length > 1,
    isCrossAccountCombined: false,
    sourcePolicies: [],
    timeframe: "—",
    timeframes: [],
    policyKinds: resolvePolicyKindsFromVersion(version),
  };
}

export function formatSimulationDeltaValue(
  delta: number | null,
  kind: "pnl" | "winRate" | "trades"
): string {
  if (delta == null) return "—";
  if (kind === "winRate") return `${formatSignedNumber(delta)}%`;
  if (kind === "trades") return formatSignedNumber(delta, 0);
  return formatSignedNumber(delta, 2);
}

const DETAIL_ENTITY_TYPES = new Set<WorkspaceNodeType>([
  "strategy",
  "report",
  "simulation",
  "combined_simulation",
  "policy",
  "combined_policy",
]);

export function workspaceNodesNeedingCardDetails(
  nodes: WorkspaceNode[] | undefined
): Array<{ type: WorkspaceNodeType; entityId: string }> {
  const seen = new Set<string>();
  const targets: Array<{ type: WorkspaceNodeType; entityId: string }> = [];
  for (const node of nodes ?? []) {
    if (!DETAIL_ENTITY_TYPES.has(node.type)) continue;
    const entityId = node.entityId.trim();
    if (!entityId) continue;
    const key = workspaceEntityCardDetailKey(node.type, entityId);
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push({ type: node.type, entityId });
  }
  return targets;
}

/** Truncate long node titles so header badges do not wrap titles to a second line. */
export function formatWorkspaceNodeCardTitle(
  title: string,
  maxLength: number = WORKSPACE_NODE_CARD_TITLE_MAX_LENGTH
): string {
  const trimmed = title.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength)}…`;
}
